// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, Tooltip } from '@mui/material';
import {
  createStepsOptions,
  StepsProvider,
  AppIcon,
  Translation,
  type TriggerProps,
} from '@shared/ui-kit';
import { Modal } from '@shared/advisory/account-workflow/layout/modal';
import { type FC } from 'react';
import { AccountWorkflowProvider } from '@shared/advisory/account-workflow/providers';
import { type AdvisoryProposalContractResponseBody } from '@shared/data-access/interfaces/api/brokerage/client-types';
import { selectClientHasNuvaraInversisAccount } from '@shared/data-access/slices/clients/clients-selectors';
import { proposalIncludesInversisCustody } from '@shared/data-access/utils';
import { useSelector } from 'react-redux';
import { AccountOpeningStepsOptions } from '@shared/advisory/account-workflow/components/account-opening/account-opening-button/account-opening-button';
import { ContractReview } from '@shared/advisory/account-workflow/components/profiling/contract-review/contract-review';
import { PersonalData } from '@shared/advisory/account-workflow/components/shared/personal-data/personal-data';
import { PendingChecklist } from '@shared/advisory/account-workflow/components/shared/pending-checklist';
import { InvestmentObjectives } from '@shared/advisory/account-workflow/components/profiling/investment-objectives/investment-objectives';
import { FinancialKnowledge } from '@shared/advisory/account-workflow/components/profiling/financial-knowledge/financial-knowledge';
import { InvestmentExperience } from '@shared/advisory/account-workflow/components/profiling/investment-experience/investment-experience';
import { FinancialSituation } from '@shared/advisory/account-workflow/components/profiling/financial-situation/financial-situation';
import { Sustainability } from '@shared/advisory/account-workflow/components/profiling/sustainability/sustainability';
import { TestResultsAndSignature } from '@shared/advisory/account-workflow/components/profiling/test-results/test-results';
import { CompletionConfirmation } from '@shared/advisory/account-workflow/components/profiling/completion-confirmation/completion-confirmation';
import type {
  AccountWorkflowSection,
  AccountWorkflowStepMeta,
  CombinedSteps,
} from '@shared/advisory/account-workflow/types';
import type { StepDefinitions } from '@shared/ui-kit/hooks';
import { isUndefined } from '@shared/utils';

const createProfilingTrigger =
  (proposal: AdvisoryProposalContractResponseBody): FC<TriggerProps> =>
  props => {
    const tooltipI18nKey =
      proposal.advisory_type === 'discretionary'
        ? 'aggregator.start-profile-test.discretionary'
        : 'aggregator.start-profile-test.advisory';

    return (
      <Tooltip
        title={<Translation i18nKey={tooltipI18nKey} ns="aggregator" />}
        placement="top"
      >
        <span>
          <Button
            {...props}
            color="secondary"
            data-testid="view-proposal-button"
            startIcon={<AppIcon icon="UserPen" size="lg" />}
          >
            <Translation
              i18nKey="aggregator.start-profile-test.label"
              ns="aggregator"
            />
          </Button>
        </span>
      </Tooltip>
    );
  };

export const ProfilingStepsOptions = createStepsOptions({
  definitions: [
    {
      id: 'contract-review',
      element: <ContractReview />,
      meta: {
        section: 'contract-review',
      },
    },
    {
      id: 'personal-data',
      element: <PersonalData />,
      meta: {
        section: 'personal-data',
      },
    },
    {
      id: 'investment-objectives',
      element: <InvestmentObjectives />,
      meta: {
        section: 'investment-objectives',
      },
    },
    {
      id: 'financial-knowledge',
      element: <FinancialKnowledge />,
      meta: {
        section: 'financial-knowledge',
      },
    },
    {
      id: 'investment-experience',
      element: <InvestmentExperience />,
      meta: {
        section: 'investment-experience',
      },
    },
    {
      id: 'financial-situation',
      element: <FinancialSituation />,
      meta: {
        section: 'financial-situation',
      },
    },
    {
      id: 'sustainability',
      element: <Sustainability />,
      meta: {
        section: 'sustainability',
      },
    },
    {
      id: 'result-and-signature',
      element: <TestResultsAndSignature />,
      meta: {
        section: 'result-and-signature',
      },
    },
    {
      id: 'confirmation',
      element: <CompletionConfirmation />,
      meta: {
        section: 'result-and-signature',
        hideProgressTracker: true,
        hideHeaderLogo: true,
      },
    },
  ],
  defaultStep: 'contract-review',
});

const combinedAccountOpeningSteps = AccountOpeningStepsOptions.definitions
  .filter(definition => definition.id !== 'next-steps')
  .map(definition => ({
    id: definition.id as CombinedSteps,
    element: definition.element,
    meta: {
      section: definition.meta!.section as AccountWorkflowSection,
      phase: 'account-opening' as const,
    },
  }));

const combinedProfilingSteps = ProfilingStepsOptions.definitions
  .filter(definition => definition.id !== 'personal-data')
  .map(definition => ({
    id: definition.id as CombinedSteps,
    element: definition.element,
    meta: {
      section: definition.meta!.section as AccountWorkflowSection,
      phase: 'profiling' as const,
      ...(!isUndefined(definition.meta?.hideProgressTracker) && {
        hideProgressTracker: definition.meta.hideProgressTracker,
      }),
      ...(!isUndefined(definition.meta?.hideHeaderLogo) && {
        hideHeaderLogo: definition.meta.hideHeaderLogo,
      }),
    },
  }));

export const CombinedProfilingStepsOptions = createStepsOptions<
  AccountWorkflowStepMeta,
  CombinedSteps
>({
  definitions: [
    {
      id: 'pending-checklist',
      element: <PendingChecklist />,
      meta: {
        section: 'pending-checklist',
        hideProgressTracker: true,
        hideHeaderLogo: true,
      },
    },
    ...combinedAccountOpeningSteps,
    ...combinedProfilingSteps,
  ] as StepDefinitions<AccountWorkflowStepMeta, CombinedSteps>,
  defaultStep: 'pending-checklist',
});

export interface ProfilingButtonProps {
  proposal: AdvisoryProposalContractResponseBody;
}

export const ProfilingButton: FC<ProfilingButtonProps> = ({ proposal }) => {
  const hasExistingInversisAccount = useSelector(
    selectClientHasNuvaraInversisAccount,
  );
  const skipAccountOpening =
    hasExistingInversisAccount || !proposalIncludesInversisCustody(proposal);

  return (
    <StepsProvider {...CombinedProfilingStepsOptions}>
      <AccountWorkflowProvider
        workflow="combined"
        proposal={proposal}
        hasExistingInversisAccount={skipAccountOpening}
      >
        <Modal
          Trigger={createProfilingTrigger(proposal)}
          modalTestId="onboarding-modal"
        />
      </AccountWorkflowProvider>
    </StepsProvider>
  );
};
